import React from "react"
import { Link } from "gatsby"
import { ThemeProvider } from "styled-components"
import componentThemeExtension from "utils/componentThemeExtension"
import { useIsHamburgerLargeOrHigher } from "utils/devices"

import {
  SliderContainer,
  SliderImageSection,
  SliderContentSection,
  SliderContent,
  SliderPreHeadingContainer,
  SliderPreHeadingText,
  SliderPreHeadingLogo,
  SliderHeading,
  SliderBody,
  SliderCardsRow,
  SliderCard,
  SliderCardTitle,
  SliderCardAuthor,
  SliderCollapsedOverlay,
  SliderCollapsedTitle,
  SliderTitleTop,
  SliderTitleBottom,
  SliderMobileStackedContainer,
  SliderMobileTopRow,
  SliderMobileImageSection,
  SliderMobileContentSection,
  SliderMobileCardsSection,
  SliderMobileCard,
  SliderButton,
} from "./styles"

const SliderButtonComponent = ({ component, sliderIndex }) => {
  const { text, targetUrl } = component

  const handleClick = () => {
    if (targetUrl) {
      window.open(targetUrl, "_blank")
    }
  }

  return (
    <SliderButton onClick={handleClick} sliderIndex={sliderIndex}>
      {text}
    </SliderButton>
  )
}

// Helper function to get background image
const getBackgroundImage = (items) =>
  items[0]?.imageCrops?.crops?.[0]?.path ||
  items[0]?.image?.[0]?.secure_url ||
  (items[0]?.article?.previewImage &&
    (() => {
      const previewData = JSON.parse(items[0].article.previewImage)
      const imagePath = previewData?.crops?.[0]?.path || previewData?.path
      if (imagePath?.startsWith("https://")) {
        return imagePath
      }
      if (imagePath?.startsWith("img/")) {
        return `https://mindbodygreen-res.cloudinary.com/image/upload/${imagePath.replace(
          "img/",
          "",
        )}`
      }
      return imagePath
    })())

const Slider = ({
  isExpandedSlider,
  sliderIndex,
  items,
  preHeadingText,
  subheadingLogo,
  title,
  bodyText,
  subheading,
  button,
  backgroundColor,
  onTop,
  theme,
}) => {
  const isHamburgerLarge = !useIsHamburgerLargeOrHigher()

  // Mobile layout (unchanged)
  if (isHamburgerLarge) {
    return (
      <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme || null)}>
        <SliderMobileStackedContainer sliderIndex={sliderIndex}>
          <SliderMobileTopRow>
            <SliderMobileImageSection backgroundImage={getBackgroundImage(items)} />
            <SliderMobileContentSection>
              <SliderPreHeadingContainer>
                <SliderPreHeadingText sliderIndex={sliderIndex}>
                  {preHeadingText &&
                    preHeadingText.childMarkdownRemark.html.replace(/(<([^>]+)>)/gi, "")}
                  {subheadingLogo?.length ? " | PRESENTED BY" : ""}
                </SliderPreHeadingText>
                {subheadingLogo?.length && (
                  <SliderPreHeadingLogo
                    src={subheadingLogo[0]?.secure_url || subheadingLogo[0]?.url}
                    alt="Presented by"
                  />
                )}
              </SliderPreHeadingContainer>
              <SliderHeading
                sliderIndex={sliderIndex}
                dangerouslySetInnerHTML={{ __html: title }}
              />
              {bodyText && (
                <SliderBody
                  sliderIndex={sliderIndex}
                  dangerouslySetInnerHTML={{ __html: bodyText.childMarkdownRemark.html }}
                />
              )}
              {subheading && (
                <SliderBody
                  sliderIndex={sliderIndex}
                  dangerouslySetInnerHTML={{ __html: subheading.childMarkdownRemark.html }}
                />
              )}
              {button && <SliderButtonComponent component={button} sliderIndex={sliderIndex} />}
            </SliderMobileContentSection>
          </SliderMobileTopRow>

          <SliderMobileCardsSection>
            {items.slice(0, 2).map((item) => {
              const articleUrl = item.article?.url ? `/articles/${item.article.url}` : null
              const cardContent = (
                <SliderMobileCard key={item.id} sliderIndex={sliderIndex}>
                  <SliderCardTitle sliderIndex={sliderIndex}>
                    {item.headlineCard ||
                      item.article?.titles?.site ||
                      item.article?.titles?.body ||
                      "Article Title"}
                  </SliderCardTitle>
                  <SliderCardAuthor sliderIndex={sliderIndex}>
                    By {item.byline || item.article?.authors?.[0]?.name || "Author Name"}
                  </SliderCardAuthor>
                </SliderMobileCard>
              )

              return articleUrl ? (
                <Link key={item.id} to={articleUrl} style={{ textDecoration: "none" }}>
                  {cardContent}
                </Link>
              ) : (
                cardContent
              )
            })}
          </SliderMobileCardsSection>
        </SliderMobileStackedContainer>
      </ThemeProvider>
    )
  }

  // Desktop unified layout with smooth transitions
  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme || null)}>
      <SliderContainer isExpanded={isExpandedSlider} sliderIndex={sliderIndex}>
        {/* Image Section */}
        <SliderImageSection
          isExpanded={isExpandedSlider}
          sliderIndex={sliderIndex}
          backgroundImage={getBackgroundImage(items)}
        />

        {/* Static Content - Always Visible */}
        <SliderContentSection isExpanded={isExpandedSlider} sliderIndex={sliderIndex}>
          <SliderContent isExpanded={isExpandedSlider}>
            {/* Pre-heading and heading always visible */}
            <SliderPreHeadingContainer>
              <SliderPreHeadingText sliderIndex={sliderIndex}>
                {preHeadingText &&
                  preHeadingText.childMarkdownRemark.html.replace(/(<([^>]+)>)/gi, "")}
                {subheadingLogo?.length ? " | PRESENTED BY" : ""}
              </SliderPreHeadingText>
              {subheadingLogo?.length && (
                <SliderPreHeadingLogo
                  src={subheadingLogo[0]?.secure_url || subheadingLogo[0]?.url}
                  alt="Presented by"
                />
              )}
            </SliderPreHeadingContainer>
            <SliderHeading
              isExpanded={isExpandedSlider}
              sliderIndex={sliderIndex}
              dangerouslySetInnerHTML={{ __html: title }}
            />

            {/* Body text appears after transition completes */}
            {bodyText && (
              <SliderBody
                isExpanded={isExpandedSlider}
                sliderIndex={sliderIndex}
                dangerouslySetInnerHTML={{ __html: bodyText.childMarkdownRemark.html }}
              />
            )}
            {subheading && (
              <SliderBody
                isExpanded={isExpandedSlider}
                sliderIndex={sliderIndex}
                dangerouslySetInnerHTML={{ __html: subheading.childMarkdownRemark.html }}
              />
            )}
            <SliderButtonComponent component={button} sliderIndex={sliderIndex} />
          </SliderContent>

          {/* Article Cards - appear after transition completes */}
          <SliderCardsRow isExpanded={isExpandedSlider}>
            {items.slice(0, 2).map((item) => {
              const articleUrl = item.article?.url ? `/articles/${item.article.url}` : null
              const cardContent = (
                <SliderCard key={item.id} sliderIndex={sliderIndex}>
                  <SliderCardTitle sliderIndex={sliderIndex}>
                    {item.headlineCard ||
                      item.article?.titles?.site ||
                      item.article?.titles?.body ||
                      "Article Title"}
                  </SliderCardTitle>
                  <SliderCardAuthor sliderIndex={sliderIndex}>
                    By {item.byline || item.article?.authors?.[0]?.name || "Author Name"}
                  </SliderCardAuthor>
                </SliderCard>
              )

              return articleUrl ? (
                <Link key={item.id} to={articleUrl} style={{ textDecoration: "none" }}>
                  {cardContent}
                </Link>
              ) : (
                cardContent
              )
            })}
          </SliderCardsRow>
        </SliderContentSection>

        {/* Collapsed State Overlay - CSS-delay controlled */}
        <SliderCollapsedOverlay isExpanded={isExpandedSlider}>
          <SliderTitleTop sliderIndex={sliderIndex}>
            <div
              dangerouslySetInnerHTML={{
                __html: preHeadingText
                  ? preHeadingText.childMarkdownRemark.html
                      .split("<p>")[1]
                      .split("</p>")[0]
                      .split(" ")[0]
                  : "DIGITAL",
              }}
            />
          </SliderTitleTop>
          <SliderCollapsedTitle dangerouslySetInnerHTML={{ __html: title }} />
          <SliderTitleBottom sliderIndex={sliderIndex}>
            <div
              dangerouslySetInnerHTML={{
                __html: preHeadingText
                  ? preHeadingText.childMarkdownRemark.html
                      .split("<p>")[1]
                      .split("</p>")[0]
                      .split(" ")[1]
                  : "ISSUE",
              }}
            />
          </SliderTitleBottom>
        </SliderCollapsedOverlay>
      </SliderContainer>
    </ThemeProvider>
  )
}

export default Slider
